import './index.css'
import { Button, Dropdown, Popconfirm, Space, Table, Tooltip } from 'antd'
import type { ColumnsType, ColumnType } from 'antd/lib/table'
import type { ItemType } from 'antd/lib/menu/hooks/useItems'
import CIMSLink from 'components/CIMSLink'
import { CIMSTag } from 'components/CIMSTag'
import { useCheckPermissions } from 'hooks/useCheckPermission'
import { useRouteMatches } from 'hooks/useRouteMatches'
import type { RenderedCell } from 'rc-table/lib/interface'
import type { Key, ReactNode } from 'react'
import {
  isValidElement,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { removalTooltip } from 'utils/removalTooltip'
import type { WorkflowRecord } from '../../types'
import type {
  RecordsTableActions,
  RecordsTableActionsResolverUtils,
  RecordsTableDirectAction,
  RecordsTableMenuItemAction,
  RecordsTableMenuTrigger,
  RecordsTablePagination,
  RecordsTableProps,
} from './types'

const authorizationDisabledMessage =
  "You don't have permission to perform this action."

const actionsResolverUtils: RecordsTableActionsResolverUtils = {
  include: (included, config) => (included ? config : undefined),
}

/**
 * Shrinks to the content so surrounding cell whitespace reaches Ant's row-click
 * expansion. Stopping propagation also covers overlays a cell opens, since
 * React bubbles events through portals.
 */
function RecordsTableCell({ children }: { children: ReactNode }) {
  return (
    <div
      onClick={(event) => event.stopPropagation()}
      style={{ cursor: 'auto', display: 'inline-block', maxWidth: '100%' }}
    >
      {children}
    </div>
  )
}

/** Wraps cell content without losing custom cell props or built-in controls. */
function withRecordsTableCells(
  columns: ColumnsType<WorkflowRecord>
): ColumnsType<WorkflowRecord> {
  return columns.map((column) => {
    if (column === Table.EXPAND_COLUMN || column === Table.SELECTION_COLUMN)
      return column
    if ('children' in column)
      return { ...column, children: withRecordsTableCells(column.children) }

    const { render } = column
    return {
      ...column,
      render: (value: ReactNode, record: WorkflowRecord, index: number) => {
        const rendered = render ? render(value, record, index) : value
        if (isRenderedCell(rendered)) {
          return {
            ...rendered,
            children: <RecordsTableCell>{rendered.children}</RecordsTableCell>,
          }
        }
        return <RecordsTableCell>{rendered}</RecordsTableCell>
      },
    }
  })
}

/** Finds data cells eligible for removed styling, excluding table controls. */
function dataColumns(
  columns: ColumnsType<WorkflowRecord>
): ColumnType<WorkflowRecord>[] {
  return columns.flatMap((column) => {
    if (
      column === Table.EXPAND_COLUMN ||
      column === Table.SELECTION_COLUMN ||
      column.key === 'actions'
    )
      return []
    if ('children' in column) return dataColumns(column.children)
    return [column]
  })
}

/** Distinguishes Ant's cell-props result from ordinary React content. */
function isRenderedCell(
  value: ReactNode
): value is RenderedCell<WorkflowRecord> {
  return Boolean(
    value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      !isValidElement(value) &&
      ('children' in value || 'props' in value)
  )
}

/** Marks removed records while retaining each column's content and cell props. */
function withRemovedState(columns: ColumnsType<WorkflowRecord>) {
  const leaves = dataColumns(columns)
  const labelColumn = leaves.find(({ key }) => key === 'status') ?? leaves[0]
  const decorate = (
    columns: ColumnsType<WorkflowRecord>
  ): ColumnsType<WorkflowRecord> =>
    columns.map((column) => {
      if ('children' in column) {
        return { ...column, children: decorate(column.children) }
      }
      if (!leaves.includes(column)) return column
      return {
        ...column,
        render: (value: ReactNode, record, index) => {
          const rendered = column.render
            ? column.render(value, record, index)
            : value
          if (!record.deleted) return rendered
          const tooltip = removalTooltip({
            removedBy: record.deletedBy?.username,
            removedDate: record.deletedDate,
          })
          const content = (children: ReactNode) => (
            <>
              <div className="records-table-removed-content">{children}</div>
              {column === labelColumn && (
                <CIMSTag
                  className="records-table-removed-label"
                  color="red"
                  tabIndex={tooltip ? 0 : undefined}
                  tooltip={tooltip}
                >
                  Removed
                </CIMSTag>
              )}
            </>
          )
          if (isRenderedCell(rendered)) {
            return { ...rendered, children: content(rendered.children) }
          }
          return content(rendered)
        },
      }
    })
  return decorate(columns)
}

/**
 * Limits a removed record to restore and SharePoint actions marked
 * `availableWhenDeleted`. Active records keep every resolved action.
 *
 * The table applies this before deciding whether to show the Actions column,
 * so actions hidden on removed rows cannot leave an empty column.
 */
function applicableRecordActions(
  record: WorkflowRecord,
  actions: RecordsTableActions
): RecordsTableActions {
  if (!record.deleted) return actions

  return {
    lifecycle: actions.lifecycle
      ? { ...actions.lifecycle, active: false }
      : undefined,
    sharePoint: actions.sharePoint
      ? {
          ...actions.sharePoint,
          items: actions.sharePoint.items.map((action) =>
            action?.availableWhenDeleted ? action : undefined
          ),
        }
      : undefined,
  }
}

function configuredActions<Action>(actions: Array<Action | undefined>) {
  return actions.filter((action): action is Action => action !== undefined)
}

/** Uses the action's explanation before falling back to a generic permission message. */
function actionDisabledMessages(
  action: Pick<RecordsTableDirectAction, 'disabled'>,
  authorized: boolean
) {
  if (action.disabled !== false) return [action.disabled.message]
  if (!authorized) return [authorizationDisabledMessage]
  return []
}

function menuItem(
  action: RecordsTableMenuItemAction,
  authorized: boolean,
  key: Key
): ItemType {
  const disabledMessages = actionDisabledMessages(action, authorized)
  const href = disabledMessages.length === 0 ? action.href : undefined
  const onClick = disabledMessages.length === 0 ? action.onClick : undefined

  return {
    disabled: disabledMessages.length > 0,
    key,
    label: (
      <Tooltip
        placement="right"
        title={disabledMessages.length ? disabledMessages.join(' ') : undefined}
      >
        {href ? (
          <CIMSLink className="records-table-menu-item" external href={href}>
            {action.label}
          </CIMSLink>
        ) : (
          <span className="records-table-menu-item">{action.label}</span>
        )}
      </Tooltip>
    ),
    onClick: onClick ? () => void onClick() : undefined,
  }
}

function MenuRecordAction({
  groups,
  label,
  trigger,
}: {
  groups: Array<{ items: ItemType[]; label: string }>
  label: string
  trigger: RecordsTableMenuTrigger
}) {
  const button = (
    <Button
      className="records-table-action"
      disabled={trigger.disabled !== false}
      type="text"
    >
      {label}
    </Button>
  )

  if (trigger.disabled !== false) {
    return (
      <Tooltip title={trigger.disabled.message}>
        <span className="records-table-action-wrapper">{button}</span>
      </Tooltip>
    )
  }

  return (
    <Dropdown
      menu={{
        items: groups.map(({ items, label: groupLabel }) => ({
          children: items,
          key: groupLabel,
          label: groupLabel,
          type: 'group',
        })),
      }}
      overlayClassName="records-table-action-menu"
      trigger={['click']}
    >
      {button}
    </Dropdown>
  )
}

function RecordActions({
  actions,
  record,
}: {
  actions: RecordsTableActions
  record: WorkflowRecord
}) {
  const can = useCheckPermissions(record)
  const [removeConfirmationVisible, setRemoveConfirmationVisible] =
    useState(false)
  const workflowAdvance = configuredActions(actions.workflow?.advance ?? [])
  const workflowRevert = configuredActions(actions.workflow?.revert ?? [])
  const sharePointItems = configuredActions(actions.sharePoint?.items ?? [])
  const workflow =
    actions.workflow &&
    (workflowAdvance.length > 0 || workflowRevert.length > 0)
      ? {
          ...actions.workflow,
          advance: workflowAdvance,
          revert: workflowRevert,
        }
      : undefined
  const sharePoint =
    actions.sharePoint && sharePointItems.length > 0
      ? { ...actions.sharePoint, items: sharePointItems }
      : undefined
  const visibleActions: Array<{
    action: RecordsTableDirectAction
    label: string
    name: 'edit' | 'lifecycle'
  }> = []
  if (actions.edit) {
    visibleActions.push({
      action: actions.edit,
      label: 'Edit',
      name: 'edit',
    })
  }
  if (actions.lifecycle) {
    const action = actions.lifecycle.active
      ? actions.lifecycle.remove
      : actions.lifecycle.restore
    visibleActions.push({
      action,
      label: actions.lifecycle.active ? 'Remove' : 'Restore',
      name: 'lifecycle',
    })
  }

  return (
    <Space
      className="records-table-actions"
      direction="vertical"
      onClick={(event) => event.stopPropagation()}
      size={8}
    >
      {workflow && (
        <MenuRecordAction
          groups={[
            ...(workflow.advance.length > 0
              ? [
                  {
                    items: workflow.advance.map((action, index) =>
                      menuItem(
                        action,
                        can(action.permission),
                        `${record.id}:workflow:advance:${index}`
                      )
                    ),
                    label: 'Advance',
                  },
                ]
              : []),
            ...(workflow.revert.length > 0
              ? [
                  {
                    items: workflow.revert.map((action, index) =>
                      menuItem(
                        action,
                        can(action.permission),
                        `${record.id}:workflow:revert:${index}`
                      )
                    ),
                    label: 'Revert',
                  },
                ]
              : []),
          ]}
          label="Workflow"
          trigger={workflow}
        />
      )}
      {sharePoint && (
        <MenuRecordAction
          groups={[
            {
              items: sharePoint.items.map((action, index) =>
                menuItem(
                  action,
                  can(action.permission),
                  `${record.id}:sharepoint:${index}`
                )
              ),
              label: 'SharePoint',
            },
          ]}
          label="SharePoint"
          trigger={sharePoint}
        />
      )}
      {visibleActions.map(({ action, label, name }) => {
        const disabledMessages = actionDisabledMessages(
          action,
          can(action.permission)
        )
        const confirmRemove =
          name === 'lifecycle' && actions.lifecycle?.active === true
        const disabledReasonId = `${record.id}-${name}-disabled-reason`
        const button = (
          <Button
            className="records-table-action"
            disabled={disabledMessages.length > 0}
            onClick={
              confirmRemove
                ? () => setRemoveConfirmationVisible(true)
                : () => void action.onClick()
            }
            type="text"
          >
            {label}
          </Button>
        )

        return disabledMessages.length ? (
          <Tooltip
            id={disabledReasonId}
            key={name}
            title={disabledMessages.join(' ')}
            // The disabled button cannot receive focus, so its wrapper
            // exposes the reason.
            trigger={['hover', 'focus']}
          >
            <span
              aria-describedby={disabledReasonId}
              aria-label={`${label} unavailable`}
              className="records-table-action-wrapper"
              role="group"
              tabIndex={0}
            >
              {button}
            </span>
          </Tooltip>
        ) : confirmRemove ? (
          <Popconfirm
            key={name}
            onCancel={() => setRemoveConfirmationVisible(false)}
            onConfirm={async () => {
              setRemoveConfirmationVisible(false)
              await action.onClick()
            }}
            onOpenChange={setRemoveConfirmationVisible}
            placement="topRight"
            title={`Are you sure you want to remove ${record.recordName}?`}
            open={removeConfirmationVisible}
          >
            <span className="records-table-action-wrapper">{button}</span>
          </Popconfirm>
        ) : (
          <span className="records-table-action-wrapper" key={name}>
            {button}
          </span>
        )
      })}
    </Space>
  )
}

/**
 * Presents workflow records with the controls and table behavior used by the
 * Records page.
 */
function RecordsTable({
  actions,
  columns,
  createAction,
  deepLink,
  expandable,
  loading,
  onReload,
  records,
}: RecordsTableProps) {
  const { replaceHistory } = useRouteMatches()
  const [showDeletedRecords, setShowDeletedRecords] = useState(false)
  const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([])
  const [pageSize, setPageSize] = useState(25)
  const hasDeletedRecords = records.some(({ deleted }) => deleted)
  const rowExpandable = expandable.rowExpandable
  const visibleRecords = useMemo(() => {
    if (deepLink) return records.filter(({ id }) => id === deepLink)
    return showDeletedRecords
      ? records
      : records.filter(({ deleted }) => !deleted)
  }, [deepLink, records, showDeletedRecords])
  const [currentRecordCount, setCurrentRecordCount] = useState(
    visibleRecords.length
  )
  const hasActiveFilters = useRef(false)
  const pendingScrollRowKey = useRef<Key | undefined>(undefined)
  const tableContainer = useRef<HTMLDivElement>(null)

  useEffect(() => {
    /*
     * Ant preserves its filters when dataSource changes. Keep the matching
     * count as well so a filtered-empty table does not regain horizontal
     * scrolling while refreshed records are still filtered out.
     */
    if (!hasActiveFilters.current) setCurrentRecordCount(visibleRecords.length)
  }, [visibleRecords])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Stop-gap for existing behavior.
    if (!hasDeletedRecords) setShowDeletedRecords(false)
  }, [hasDeletedRecords])

  useEffect(() => {
    if (!deepLink) return
    const record = records.find(({ id }) => id === deepLink)
    if (!record || rowExpandable?.(record) === false) return

    pendingScrollRowKey.current = record.id
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Stop-gap for existing behavior.
    setExpandedRowKeys((keys) =>
      keys.length === 1 && keys[0] === record.id ? keys : [record.id]
    )
  }, [deepLink, records, rowExpandable])

  /*
   * Expanded details can render below Ant's 60vh table viewport. Once the
   * controlled keys have rendered, align the triggering record within the
   * table body instead of moving the page with scrollIntoView.
   */
  useLayoutEffect(() => {
    const rowKey = pendingScrollRowKey.current
    if (rowKey === undefined || !expandedRowKeys.includes(rowKey)) return

    pendingScrollRowKey.current = undefined

    const container = tableContainer.current
    const tableBody = container?.querySelector<HTMLElement>('.ant-table-body')
    const recordRow = [
      ...(container?.querySelectorAll('tr[data-row-key]') ?? []),
    ].find((row) => row.getAttribute('data-row-key') === String(rowKey))

    if (!tableBody || !recordRow) return

    const offset =
      recordRow.getBoundingClientRect().top -
      tableBody.getBoundingClientRect().top
    const top = tableBody.scrollTop + offset
    if (typeof tableBody.scrollTo === 'function') {
      tableBody.scrollTo({
        behavior: 'smooth',
        left: tableBody.scrollLeft,
        top,
      })
    } else {
      tableBody.scrollTop = top
    }
  }, [expandedRowKeys])

  const resolvedExpandable = useMemo(
    () => ({
      ...expandable,
      expandRowByClick: true,
      expandedRowKeys,
      onExpandedRowsChange: (keys: readonly Key[]) => {
        expandable.onExpandedRowsChange?.(keys)
        /*
         * The key added to the controlled set identifies an expansion. A
         * collapse adds no key, so it must not trigger another scroll.
         */
        pendingScrollRowKey.current = keys.find(
          (key) => !expandedRowKeys.includes(key)
        )
        setExpandedRowKeys([...keys])
      },
    }),
    [expandable, expandedRowKeys]
  )
  const actionsByRecord = useMemo(
    () =>
      new Map(
        records.map((record) => [
          record,
          applicableRecordActions(
            record,
            actions(record, actionsResolverUtils)
          ),
        ])
      ),
    [actions, records]
  )
  const resolvedColumns = useMemo(() => {
    const decoratedColumns = withRemovedState(columns)
    const hasVisibleAction = [...actionsByRecord.values()].some(
      (recordActions) =>
        recordActions.edit !== undefined ||
        recordActions.lifecycle !== undefined ||
        Boolean(
          recordActions.workflow?.advance.some(
            (action) => action !== undefined
          ) ||
            recordActions.workflow?.revert.some(
              (action) => action !== undefined
            ) ||
            recordActions.sharePoint?.items.some(
              (action) => action !== undefined
            )
        )
    )

    if (!hasVisibleAction) return withRecordsTableCells(decoratedColumns)

    return withRecordsTableCells([
      ...decoratedColumns,
      {
        fixed: 'right' as const,
        key: 'actions',
        render: (_value: unknown, record: WorkflowRecord) => (
          <RecordActions
            actions={actionsByRecord.get(record) ?? {}}
            record={record}
          />
        ),
        title: 'Actions',
        width: 120,
      },
    ])
  }, [actionsByRecord, columns])
  const pagination: RecordsTablePagination = {
    onChange: (_page, nextPageSize) => setPageSize(nextPageSize),
    pageSize,
    pageSizeOptions: [25, 50, 100],
    position: ['bottomRight'],
    showQuickJumper: true,
    showSizeChanger: true,
    showTotal: (total, [from, to]) => (
      <div style={{ marginRight: 8 }}>
        {from}-{to} of {total}
      </div>
    ),
  }
  const createDisabled = createAction?.disabled ?? false

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem 1.5rem',
          justifyContent: 'space-between',
        }}
      >
        <div>
          {createAction && (
            <Tooltip
              title={
                createDisabled === false ? undefined : createDisabled.message
              }
            >
              <Button
                disabled={createDisabled !== false || loading}
                loading={loading}
                onClick={createAction.onClick}
                type="primary"
              >
                {createAction.label}
              </Button>
            </Tooltip>
          )}
        </div>
        <div
          style={{
            alignItems: 'center',
            display: 'inline-flex',
            flexWrap: 'wrap',
            gap: '0.5rem 1rem',
          }}
        >
          {records.length > 1 && expandedRowKeys.length > 0 && (
            <Button onClick={() => setExpandedRowKeys([])}>Collapse All</Button>
          )}
          {deepLink && (
            <Button
              disabled={loading}
              onClick={() =>
                replaceHistory({ action: 'remove', key: 'deepLink' })
              }
            >
              Show All
            </Button>
          )}
          {!deepLink && hasDeletedRecords && (
            <Button
              disabled={loading}
              onClick={() => {
                setExpandedRowKeys([])
                setShowDeletedRecords((showing) => !showing)
              }}
            >
              {showDeletedRecords ? 'Hide Removed' : 'Show Removed'}
            </Button>
          )}
          <Button
            disabled={loading}
            id="reload-btn"
            loading={loading}
            onClick={() => onReload()}
          >
            Reload
          </Button>
        </div>
      </div>
      <Table<WorkflowRecord>
        ref={tableContainer}
        columns={resolvedColumns}
        dataSource={visibleRecords}
        expandable={resolvedExpandable}
        loading={loading}
        onChange={(_pagination, filters, _sorter, { currentDataSource }) => {
          hasActiveFilters.current = Object.values(filters).some((values) =>
            Boolean(values?.length)
          )
          setCurrentRecordCount(currentDataSource.length)
        }}
        onRow={(record) =>
          resolvedExpandable.rowExpandable?.(record)
            ? { style: { cursor: 'pointer' } }
            : {}
        }
        pagination={pagination}
        rowKey="id"
        rowClassName={(record) =>
          record.deleted
            ? 'records-table-row records-table-record-deleted'
            : 'records-table-row'
        }
        scroll={{
          x: currentRecordCount ? 'max-content' : undefined,
          y: '60vh',
        }}
        tableLayout="fixed"
      />
    </Space>
  )
}

export default RecordsTable
